import { ReactElement } from 'react';
import { Flex, Box } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { Form, useFormik, FormikProvider, useField } from 'formik';
import styled from 'styled-components';
import { useToasts } from 'react-toast-notifications';

import { Route } from 'react-router-dom';
import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import { PageTitle, SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import Button from 'packages/kakapo-ui-kit/components/editors/Button';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import SettingsTable, { SettingsRow } from 'packages/kakapo-ui-kit/components/common/SettingsTable';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import usePreferredTranslation from 'packages/kakapo-ui-application/hooks/usePreferredTranslation';
import Toggle from 'packages/kakapo-ui-kit/components/forms/controls/Toggle';
import MultilingualQuill from 'packages/kakapo-ui-kit/components/forms/controls/MultilingualQuill';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import TooltipTarget from 'packages/kakapo-ui-kit/components/tooltips/TooltipTarget';
import CompetencyLevelSetModal from 'packages/kakapo-ui-settings-application/pages/evaluations/competency-level-sets/CompetencyLevelSetModal';
import { validator } from 'packages/kakapo-ui-services/factories/ValidatorFactory';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import { ActionArchiveListItem } from 'packages/kakapo-ui-kit/components/actions/ActionListItem';
import useBreadcrumb from 'packages/kakapo-ui-kit/components/breadcrumb/useBreadcrumb';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

function CompetencyLevel(props: { index: number; option: graphql.CompetencyLevelOption }) {
	const { translate } = usePreferredTranslation();

	const [field, , { setValue }] = useField<boolean>(`options.${props.index}.enabled`);

	return (
		<SettingsRow>
			<Flex flexDirection="row" alignItems="center">
				<Box mr={3} flexShrink={0}>
					<Toggle
						checked={field.value}
						name="checkbox"
						value="1"
						unspaced
						onChange={() => {
							setValue(!field.value);
						}}
					/>
				</Box>
				<Box width={150} flexShrink={0}>
					<Paragraph>{translate(props.option.level.title)}</Paragraph>
				</Box>
				<Box flexGrow={1}>
					{field.value && <MultilingualQuill name={`options.${props.index}.criteria`} />}
				</Box>
			</Flex>
		</SettingsRow>
	);
}

function CompetencyLevelSetSelector(props: { competency?: graphql.Competency }) {
	const { t } = useTranslation();

	const [field, , { setValue }] = useField<graphql.CompetencyLevelSet | undefined>(
		'competencyLevelSet',
	);

	const [, , { setValue: setOptions }] = useField<graphql.CompetencyLevelOption[]>('options');

	const { data, loading, refetch } = graphql.useCompetencyLevelSetsQuery({
		variables: {
			isArchived: false,
			first: 100,
		},
		fetchPolicy: 'cache-and-network',
	});

	const { translate } = usePreferredTranslation();

	const route = (
		<Route
			path={[
				`/settings/competencies/:id/levels`,
				`/v2/settings/competencies/:id/levels`,
				`/v2/settings/competencies/new/levels`,
			]}
			exact
		>
			<CompetencyLevelSetModal
				onSave={async (set) => {
					await refetch();

					if (!props.competency) {
						setLevelSet(set);
					}
				}}
				returnUrl={`/settings/competencies/${props.competency?.id ?? 'create'}`}
				set={props.competency && props.competency.competencyLevelSet}
			/>
		</Route>
	);

	if (loading) {
		return (
			<>
				{
					<Route path={`/settings/competencies/:id/levels`} exact>
						<CompetencyLevelSetModal
							onSave={async (set) => {
								await refetch();

								if (!props.competency) {
									setLevelSet(set);
								}
							}}
							returnUrl={`/settings/competencies/${props.competency?.id ?? 'create'}`}
							set={props.competency && props.competency.competencyLevelSet}
						/>
					</Route>
				}
				<Spinner isLoading verticalAlignment={0} size="18px" />
			</>
		);
	}

	const setLevelSet = (set: graphql.CompetencyLevelSet) => {
		setValue(set);
		setOptions(
			set.levels.map((level) => ({
				level,
				enabled: true,
				criteria: [],
			})),
		);
	};

	const select = (
		<Select
			isDisabled={!!props.competency}
			small
			unspaced
			onChange={(option) => {
				const set = data?.competencyLevelSets.edges.find((edge) => edge.node.id === option?.value)
					?.node as graphql.CompetencyLevelSet;

				if (!set) {
					return;
				}

				setLevelSet(set);
			}}
			value={
				field.value && {
					label: translate(field.value.title),
					value: field.value.id,
				}
			}
			options={[
				{
					options:
						data?.competencyLevelSets.edges.map((edge) => ({
							value: edge.node.id,
							label: translate(edge.node.title),
						})) ?? [],
				},
			]}
		/>
	);

	if (props.competency) {
		return (
			<>
				{route}
				<TooltipTarget title={t('common.settings.competencies.edit.readonly')}>
					{select}
				</TooltipTarget>
			</>
		);
	}

	return (
		<>
			{route}
			{select}
		</>
	);
}

const Anchor = styled.a`
	color: ${(props) => props.theme.colors.primary};
	cursor: pointer;
	text-decoration: underline;
`;

function CompetencyLevelSetChooser() {
	const { t } = useTranslation();
	const history = useHistory();

	return (
		<>
			<Box my={4}>
				<Box mb={3}>
					<Paragraph color="slateBlue">
						{t('common.settings.competencies.create.explanation')}{' '}
						<Anchor>{t('common.learnMore')}</Anchor>
					</Paragraph>
				</Box>
				<Box mb={3}>
					<Paragraph color="slateBlue">
						{t('common.settings.competencies.create.encouragement')}
					</Paragraph>
				</Box>
				<Flex alignItems="center">
					<Paragraph color="slateBlue">
						{t('common.settings.competencies.create.selectExisting')}
					</Paragraph>
					<Box width={200} mx={3}>
						<CompetencyLevelSetSelector />
					</Box>
					<Paragraph color="slateBlue">{t('common.settings.competencies.create.or')}</Paragraph>
					<Box mx={3}>
						<Button size={32} onClick={() => history.push(`/settings/competencies/create/levels`)}>
							{t('common.settings.competencies.create.createLevelSet')}
						</Button>
					</Box>
				</Flex>
			</Box>
		</>
	);
}

function CompetencyEditor(props: { competency?: graphql.Competency }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { localize } = useLocalize();

	useBreadcrumb(t(`common.settings.competencies.${props.competency ? 'edit' : 'create'}.title`));

	const useCompetencyGoals = useFeature('2021.competencyGoals');

	const formik = useFormik({
		initialValues: {
			competencyLevelSet: props.competency?.competencyLevelSet,
			title: props.competency?.title ?? [],
			description: props.competency?.description ?? [],
			options:
				props.competency?.levels.map((level) => ({
					...level,
					criteria: level.criteria ?? [],
				})) ?? [],
			jobTitles: props.competency?.jobTitles.map((jobTitle) => jobTitle.id) ?? [],
			attachableToGoal: props.competency?.attachableToGoal ?? false,
		},
		onSubmit: () => onUpdate(),
		validationSchema: validator.object({
			title: validator
				.array(
					validator.object({
						text: validator.string().min(1).max(256).required(),
					}),
				)
				.min(1),
		}),
	});

	const [error, attempt, reset] = useErrorBoundary();

	const { data: jobTitlesData, loading: jobTitlesLoading } = graphql.useJobTitlesQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const [create, { loading: createLoading }] = graphql.useCreateCompetencyMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateCompetencyMutation();
	const loading = createLoading || updateLoading || jobTitlesLoading;

	const onUpdate = attempt(async () => {
		reset();

		const input = {
			title: formik.values.title.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			description: formik.values.description?.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			levels: formik.values.options.map((option) => ({
				competencyLevelID: option.level.id,
				enabled: option.enabled,
				criteria: option.criteria?.map((string) => ({
					languageID: string.languageID,
					text: string.text,
				})),
			})),
			jobTitleIDs: formik.values.jobTitles ?? [],
			attachableToGoal: formik.values.attachableToGoal,
		};

		if (props.competency) {
			await update({
				variables: {
					input: {
						id: props.competency.id,
						...input,
					},
				},
			});
		} else {
			const { data } = await create({
				variables: {
					input: {
						competencyLevelSetID: formik.values.competencyLevelSet!.id,
						...input,
					},
				},
			});

			if (data) {
				history.push(`/settings/competencies/${data.createCompetency.competency.id}`);
			}
		}

		addToast(t('common.settings.competencies.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const history = useHistory();

	return (
		<Flex flexDirection="column" mt={3}>
			<Flex flexDirection="row" alignItems="center">
				<PageTitle>
					{t(`common.settings.competencies.${props.competency ? 'edit' : 'create'}.title`)}
				</PageTitle>
			</Flex>
			<Box mt={4} mb={2}>
				<Box mb={3}>
					<SubSectionTitle>{t('common.settings.competencies.edit.details')}</SubSectionTitle>
				</Box>
				<HorizontalRule />
			</Box>
			<GraphQLErrorMessage error={error} />
			<FormikProvider value={formik}>
				<Form>
					<DetailsTableForm
						formValues={[
							{
								label: t('common.settings.competencies.edit.name'),
								name: 'title',
								type: DetailsTableFormValueTypes.Multilingual,
							},
							{
								label: t('common.settings.competencies.edit.description'),
								name: 'description',
								type: DetailsTableFormValueTypes.MultilingualQuill,
							},
							...(useCompetencyGoals
								? [
										{
											label: t('common.settings.competencies.edit.attachableToGoal'),
											name: 'attachableToGoal',
											type: DetailsTableFormValueTypes.Switch,
										},
								  ]
								: []),
							...(formik.values.attachableToGoal
								? [
										{
											label: t('common.settings.competencies.edit.jobTitles'),
											name: 'jobTitles',
											type: DetailsTableFormValueTypes.MultiSelect,
											options: jobTitlesData?.jobTitles.edges.map(({ node }) => ({
												value: node.id,
												label: localize(node.title)?.text,
											})),
										},
								  ]
								: []),
						]}
					/>
				</Form>

				<Box mt={3}>
					{props.competency ? (
						<SettingsTable
							title={t('common.settings.competencies.edit.levels')}
							toolbar={
								<Flex alignItems="center">
									<Box width={200} mr={3}>
										<CompetencyLevelSetSelector competency={props.competency} />
									</Box>
									{formik.values.competencyLevelSet && (
										<Button
											size={32}
											onClick={() =>
												history.push(
													`/settings/competencies/${props.competency?.id ?? 'create'}/levels`,
												)
											}
										>
											{t('common.edit')}
										</Button>
									)}
								</Flex>
							}
						>
							{formik.values.options.map((option, index) => (
								<CompetencyLevel
									key={option.level.id}
									index={index}
									option={{ ...option, level: props.competency!.competencyLevelSet.levels[index] }}
								/>
							))}
						</SettingsTable>
					) : (
						<SettingsTable title={t('common.settings.competencies.edit.levels')}>
							<CompetencyLevelSetChooser />
						</SettingsTable>
					)}
				</Box>
			</FormikProvider>

			<Flex mt={4}>
				<Button
					size={32}
					onClick={() => formik.submitForm()}
					isLoading={loading}
					enabled={!!formik.values.competencyLevelSet}
				>
					{t('common.save')}
				</Button>
			</Flex>

			<Flex mt={4}>
				{props.competency && (
					<ActionArchiveListItem
						archivableItem={props.competency}
						archivableItemName="competency"
						archivableItemKey="competency"
						useArchive={graphql.useArchiveCompetencyMutation as any}
						useRestore={graphql.useRestoreCompetencyMutation as any}
					/>
				)}
			</Flex>
		</Flex>
	);
}

function CompetencyEditorID(props: { id: string }): ReactElement {
	const { data, error, loading } = graphql.useCompetencyQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return <Spinner isLoading />;
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <CompetencyEditor competency={data!.competency as graphql.Competency} />;
	}
}

export { CompetencyEditor as CompetencyCreator };

export default CompetencyEditorID;
